import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { usePageTitle } from '../../hooks/usePageTitle'
import { supabase } from '../../lib/supabase'
import {
  VStack,
  HStack,
  Card,
  Heading,
  Text,
  Badge,
  Avatar,
  Button,
  Input,
  Checkbox,
  Banner,
  Skeleton,
  ConfirmDialog,
  useToast
} from 'wss3-forge'
import {
  ArrowLeft20Regular,
  Save20Regular,
  Key20Regular,
  PersonProhibited20Regular,
  Checkmark20Regular,
  Delete20Regular,
  Shield20Regular,
  Fingerprint20Regular,
  Desktop20Regular,
  Phone20Regular,
  Tablet20Regular,
  Checkmark12Regular,
  Dismiss12Regular
} from '@fluentui/react-icons'

interface MfaFactor {
  id: string
  type: string
  friendly_name: string | null
  created_at: string
}

interface Passkey {
  id: string
  name: string
  created_at: string
  last_used_at: string | null
}

interface AppUsage {
  app_id: string
  app_name: string
  first_login_at: string | null
  last_login_at: string | null
  login_count: number
}

interface LoginEntry {
  id: string
  created_at: string
  success: boolean
  auth_method: string
  browser: string | null
  os: string | null
  device_type: string | null
  ip_address: string | null
  country: string | null
  city: string | null
  failure_reason: string | null
}

interface TrustedDevice {
  id: string
  device_name: string | null
  browser: string | null
  os: string | null
  device_type: string | null
  last_used_at: string
  expires_at: string
}

interface Subscription {
  id: string
  plan_name: string
  status: string
  current_period_start: string | null
  current_period_end: string | null
  payment_provider: string | null
  created_at: string
}

interface UserData {
  user_id: string
  email: string
  first_name: string | null
  last_name: string | null
  display_name: string | null
  avatar_url: string | null
  is_webba_member: boolean
  created_at: string
  last_sign_in_at: string | null
  email_confirmed_at: string | null
  is_admin: boolean
  is_disabled: boolean
  registered_via: string | null
  linked_providers: string[]
  mfa_enabled: boolean
  mfa_factors: MfaFactor[]
  passkeys: Passkey[]
  apps: AppUsage[]
  login_history: LoginEntry[]
  trusted_devices: TrustedDevice[]
  subscriptions: Subscription[]
}

const deviceIcons: Record<string, React.ReactNode> = {
  desktop: <Desktop20Regular />,
  mobile: <Phone20Regular />,
  tablet: <Tablet20Regular />
}

export function UserDetail() {
  const { userId } = useParams<{ userId: string }>()
  const navigate = useNavigate()
  const toast = useToast()

  const [user, setUser] = useState<UserData | null>(null)
  usePageTitle(user?.email ? `User: ${user.email}` : 'User Details')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Edit fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [isAdmin, setIsAdmin] = useState(false)
  const [isMember, setIsMember] = useState(false)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Actions
  const [sendingReset, setSendingReset] = useState(false)
  const [showDisableDialog, setShowDisableDialog] = useState(false)
  const [togglingStatus, setTogglingStatus] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const loadUser = async () => {
    if (!userId) return
    setLoading(true)
    try {
      const { data, error: err } = await supabase.rpc('admin_get_user_detail', {
        p_user_id: userId
      })

      if (err) throw err
      if (!data) {
        setError('User not found')
        return
      }

      setUser(data)
      setFirstName(data.first_name || '')
      setLastName(data.last_name || '')
      setIsAdmin(data.is_admin)
      setIsMember(data.is_webba_member)
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUser()
  }, [userId])

  useEffect(() => {
    if (!user) return
    const changed =
      firstName !== (user.first_name || '') ||
      lastName !== (user.last_name || '') ||
      isAdmin !== user.is_admin ||
      isMember !== user.is_webba_member
    setHasChanges(changed)
  }, [firstName, lastName, isAdmin, isMember, user])

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatRelativeDate = (date: string | null) => {
    if (!date) return '-'
    const d = new Date(date)
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)

    if (hours < 1) return 'Just now'
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return d.toLocaleDateString('fr-FR')
  }

  const getAuthMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      email: 'Email',
      oauth_google: 'Google',
      oauth_azure: 'Microsoft',
      oauth_github: 'GitHub',
      oauth_discord: 'Discord',
      magic_link: 'Magic Link',
      passkey: 'Passkey'
    }
    return labels[method] || method
  }

  const getProviderLabel = (provider: string) => {
    const labels: Record<string, string> = {
      email: 'Email',
      google: 'Google',
      azure: 'Microsoft',
      github: 'GitHub',
      discord: 'Discord'
    }
    return labels[provider] || provider.charAt(0).toUpperCase() + provider.slice(1)
  }

  const handleSave = async () => {
    if (!user) return
    setSaving(true)
    try {
      const { error } = await supabase.rpc('admin_update_user', {
        p_target_user_id: user.user_id,
        p_updates: {
          first_name: firstName,
          last_name: lastName,
          is_admin: isAdmin,
          is_webba_member: isMember
        }
      })
      if (error) throw error
      toast.success('User updated')
      loadUser()
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleResetPassword = async () => {
    if (!user) return
    setSendingReset(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(user.email)
      if (error) throw error
      toast.success('Reset email sent')
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setSendingReset(false)
    }
  }

  const handleToggleStatus = async () => {
    if (!user) return
    setTogglingStatus(true)
    try {
      const { error } = await supabase.rpc('admin_toggle_user_status', {
        p_target_user_id: user.user_id,
        p_disabled: !user.is_disabled
      })
      if (error) throw error
      toast.success(user.is_disabled ? 'User enabled' : 'User disabled')
      setShowDisableDialog(false)
      loadUser()
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setTogglingStatus(false)
    }
  }

  const handleDelete = async () => {
    if (!user) return
    setDeleting(true)
    try {
      const { error } = await supabase.rpc('admin_delete_user', {
        p_target_user_id: user.user_id
      })
      if (error) throw error
      toast.success('User deleted')
      navigate('/users')
    } catch (e: any) {
      toast.error(e.message)
    } finally {
      setDeleting(false)
    }
  }

  if (loading) {
    return (
      <div className="page-content">
        <VStack gap="lg">
          <Skeleton width={200} height={32} />
          <Card padding="lg">
            <VStack gap="md">
              <div style={{ borderRadius: '50%', overflow: 'hidden' }}><Skeleton width={80} height={80} /></div>
              <Skeleton width={300} height={24} />
              <Skeleton width={200} height={20} />
            </VStack>
          </Card>
        </VStack>
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="page-content">
        <VStack gap="lg">
          <Button variant="ghost" icon={<ArrowLeft20Regular />} onClick={() => navigate('/users')}>
            Back to Users
          </Button>
          <Banner variant="error" title="Error">{error || 'User not found'}</Banner>
        </VStack>
      </div>
    )
  }

  return (
    <div className="page-content">
      <VStack gap="lg">
        {/* Header */}
        <HStack style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <Button variant="ghost" icon={<ArrowLeft20Regular />} onClick={() => navigate('/users')}>
            Back to Users
          </Button>
          <HStack gap="sm" style={{ flexWrap: 'wrap' }}>
            <Button
              variant="secondary"
              icon={<Key20Regular />}
              onClick={handleResetPassword}
              loading={sendingReset}
            >
              Reset Password
            </Button>
            <Button
              variant="secondary"
              icon={user.is_disabled ? <Checkmark20Regular /> : <PersonProhibited20Regular />}
              onClick={() => setShowDisableDialog(true)}
            >
              {user.is_disabled ? 'Enable' : 'Disable'}
            </Button>
            <Button
              variant="danger"
              icon={<Delete20Regular />}
              onClick={() => setShowDeleteDialog(true)}
            >
              Delete
            </Button>
          </HStack>
        </HStack>

        {/* User Header */}
        <Card padding="lg">
          <HStack gap="lg" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
            <Avatar
              src={user.avatar_url || undefined}
              name={user.email}
              size="xl"
            />
            <VStack gap="xs" style={{ flex: 1 }}>
              <Heading level={3}>{user.email}</Heading>
              <HStack gap="sm" style={{ flexWrap: 'wrap' }}>
                {user.is_disabled && <Badge variant="error">Disabled</Badge>}
                {user.is_admin && <Badge variant="primary">Admin</Badge>}
                {user.is_webba_member && <Badge variant="success">Member</Badge>}
                {!user.is_disabled && (user.email_confirmed_at ? (
                  <Badge variant="default">Verified</Badge>
                ) : (
                  <Badge variant="warning">Unverified</Badge>
                ))}
              </HStack>
              <Text size="sm" color="muted">
                Joined {formatDate(user.created_at)} · Last login {formatRelativeDate(user.last_sign_in_at)}
              </Text>
              {user.registered_via && (
                <HStack gap="xs" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <Text size="sm" color="muted">
                    Registered via {getProviderLabel(user.registered_via)}
                  </Text>
                  {user.linked_providers && user.linked_providers.length > 1 && (
                    <>
                      <Text size="sm" color="muted">·</Text>
                      <Text size="sm" color="muted">
                        Linked: {user.linked_providers.filter(p => p !== user.registered_via).map(p => getProviderLabel(p)).join(', ')}
                      </Text>
                    </>
                  )}
                </HStack>
              )}
            </VStack>
          </HStack>
        </Card>

        {/* Two column layout */}
        <HStack gap="lg" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Left column */}
          <VStack gap="lg" style={{ flex: 1, minWidth: '300px' }}>
            {/* Edit Profile */}
            <Card padding="lg">
              <VStack gap="md">
                <HStack gap="sm" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
                  <Heading level={4}>Profile</Heading>
                  <Button
                    size="sm"
                    icon={<Save20Regular />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={!hasChanges}
                  >
                    Save
                  </Button>
                </HStack>

                {/* Personal Info */}
                <div style={{ padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                  <VStack gap="md">
                    <Text size="xs" weight="medium" color="muted" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Personal Information
                    </Text>
                    <HStack gap="md" style={{ flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <Input
                          label="First Name"
                          value={firstName}
                          onChange={(v) => setFirstName(v as string)}
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '150px' }}>
                        <Input
                          label="Last Name"
                          value={lastName}
                          onChange={(v) => setLastName(v as string)}
                        />
                      </div>
                    </HStack>
                  </VStack>
                </div>

                {/* Roles */}
                <div style={{ padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                  <VStack gap="sm">
                    <Text size="xs" weight="medium" color="muted" style={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Roles & Permissions
                    </Text>
                    <HStack gap="lg" style={{ flexWrap: 'wrap' }}>
                      <Checkbox checked={isAdmin} onChange={setIsAdmin} label="Administrator" />
                      <Checkbox checked={isMember} onChange={setIsMember} label="Webba Member" />
                    </HStack>
                  </VStack>
                </div>
              </VStack>
            </Card>

            {/* Security */}
            <Card padding="lg">
              <VStack gap="md">
                <HStack gap="sm" style={{ alignItems: 'center' }}>
                  <Shield20Regular style={{ color: 'var(--brand-primary)' }} />
                  <Heading level={4}>Security</Heading>
                </HStack>

                {/* 2FA */}
                <div style={{ padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                  <VStack gap="sm">
                    <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <VStack gap="xs">
                        <Text weight="medium">Two-Factor Authentication</Text>
                        <Text size="sm" color="muted">
                          {user.mfa_enabled
                            ? `${user.mfa_factors.length} factor(s) configured`
                            : 'Not configured'}
                        </Text>
                      </VStack>
                      <Badge variant={user.mfa_enabled ? 'success' : 'default'}>
                        {user.mfa_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </HStack>

                    {user.mfa_factors.length > 0 && (
                      <VStack gap="xs">
                        {user.mfa_factors.map((factor) => (
                          <div
                            key={factor.id}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: 'var(--bg-secondary)',
                              borderRadius: '6px'
                            }}
                          >
                            <HStack style={{ justifyContent: 'space-between' }}>
                              <Text size="sm">{factor.friendly_name || factor.type}</Text>
                              <Text size="xs" color="muted">Added {formatRelativeDate(factor.created_at)}</Text>
                            </HStack>
                          </div>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                </div>

                {/* Passkeys */}
                <div style={{ padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                  <VStack gap="sm">
                    <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <VStack gap="xs">
                        <HStack gap="sm" style={{ alignItems: 'center' }}>
                          <Fingerprint20Regular />
                          <Text weight="medium">Passkeys</Text>
                        </HStack>
                        <Text size="sm" color="muted">
                          {user.passkeys.length > 0
                            ? `${user.passkeys.length} passkey(s) registered`
                            : 'No passkeys'}
                        </Text>
                      </VStack>
                      <Badge variant={user.passkeys.length > 0 ? 'success' : 'default'}>
                        {user.passkeys.length}
                      </Badge>
                    </HStack>

                    {user.passkeys.length > 0 && (
                      <VStack gap="xs">
                        {user.passkeys.map((pk) => (
                          <div
                            key={pk.id}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: 'var(--bg-secondary)',
                              borderRadius: '6px'
                            }}
                          >
                            <HStack style={{ justifyContent: 'space-between' }}>
                              <Text size="sm">{pk.name}</Text>
                              <Text size="xs" color="muted">
                                {pk.last_used_at ? `Used ${formatRelativeDate(pk.last_used_at)}` : 'Never used'}
                              </Text>
                            </HStack>
                          </div>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                </div>

                {/* Trusted Devices */}
                <div style={{ padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                  <VStack gap="sm">
                    <VStack gap="xs">
                      <Text weight="medium">Trusted Devices</Text>
                      <Text size="sm" color="muted">
                        {user.trusted_devices.length > 0
                          ? `${user.trusted_devices.length} device(s)`
                          : 'No trusted devices'}
                      </Text>
                    </VStack>

                    {user.trusted_devices.length > 0 && (
                      <VStack gap="xs">
                        {user.trusted_devices.map((device) => (
                          <div
                            key={device.id}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: 'var(--bg-secondary)',
                              borderRadius: '6px'
                            }}
                          >
                            <HStack gap="sm" style={{ alignItems: 'center' }}>
                              <span style={{ color: 'var(--text-muted)' }}>
                                {deviceIcons[device.device_type || 'desktop'] || <Desktop20Regular />}
                              </span>
                              <VStack gap="none" style={{ flex: 1 }}>
                                <Text size="sm">{device.device_name || `${device.browser} on ${device.os}`}</Text>
                                <Text size="xs" color="muted">Last used {formatRelativeDate(device.last_used_at)}</Text>
                              </VStack>
                            </HStack>
                          </div>
                        ))}
                      </VStack>
                    )}
                  </VStack>
                </div>
              </VStack>
            </Card>

            {/* Applications */}
            <Card padding="lg">
              <VStack gap="md">
                <Heading level={4}>Applications</Heading>
                {user.apps.length === 0 ? (
                  <Text color="muted">No applications used</Text>
                ) : (
                  <VStack gap="sm">
                    {user.apps.map((app) => (
                      <div
                        key={app.app_id}
                        style={{
                          padding: '12px',
                          backgroundColor: 'var(--bg-tertiary)',
                          borderRadius: '8px'
                        }}
                      >
                        <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                          <Text weight="medium">{app.app_name}</Text>
                          <Badge size="sm">{app.login_count} logins</Badge>
                        </HStack>
                        <Text size="xs" color="muted">
                          First: {formatRelativeDate(app.first_login_at)} · Last: {formatRelativeDate(app.last_login_at)}
                        </Text>
                      </div>
                    ))}
                  </VStack>
                )}
              </VStack>
            </Card>
          </VStack>

          {/* Right column */}
          <VStack gap="lg" style={{ flex: 1, minWidth: '300px' }}>
            {/* Subscriptions */}
            <Card padding="lg">
              <VStack gap="md">
                <Heading level={4}>Subscriptions</Heading>
                {user.subscriptions.length === 0 ? (
                  <Text color="muted">No active subscriptions</Text>
                ) : (
                  <VStack gap="sm">
                    {user.subscriptions.map((sub) => (
                      <div
                        key={sub.id}
                        style={{
                          padding: '12px',
                          backgroundColor: 'var(--bg-tertiary)',
                          borderRadius: '8px'
                        }}
                      >
                        <HStack style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                          <VStack gap="xs">
                            <Text weight="medium">{sub.plan_name}</Text>
                            {sub.payment_provider && <Text size="xs" color="muted">via {sub.payment_provider}</Text>}
                          </VStack>
                          <Badge
                            variant={sub.status === 'active' ? 'success' : sub.status === 'canceled' ? 'error' : 'warning'}
                          >
                            {sub.status}
                          </Badge>
                        </HStack>
                        {sub.current_period_end && (
                          <Text size="xs" color="muted">
                            Expires {formatDate(sub.current_period_end)}
                          </Text>
                        )}
                      </div>
                    ))}
                  </VStack>
                )}
              </VStack>
            </Card>

            {/* Login History */}
            <Card padding="lg">
              <VStack gap="md">
                <Heading level={4}>Login History</Heading>
                {user.login_history.length === 0 ? (
                  <Text color="muted">No login history</Text>
                ) : (
                  <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                    <VStack gap="xs">
                      {user.login_history.map((entry) => (
                      <div
                        key={entry.id}
                        style={{
                          padding: '10px 12px',
                          backgroundColor: 'var(--bg-tertiary)',
                          borderRadius: '8px'
                        }}
                      >
                        <HStack gap="sm" style={{ alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-muted)' }}>
                            {deviceIcons[entry.device_type || 'desktop'] || <Desktop20Regular />}
                          </span>
                          <VStack gap="none" style={{ flex: 1 }}>
                            <HStack gap="sm" style={{ alignItems: 'center' }}>
                              <Text size="sm">
                                {entry.browser || 'Unknown'} on {entry.os || 'Unknown'}
                              </Text>
                              {entry.success ? (
                                <Checkmark12Regular style={{ color: 'var(--color-success)' }} />
                              ) : (
                                <Dismiss12Regular style={{ color: 'var(--color-error)' }} />
                              )}
                            </HStack>
                            <HStack gap="sm">
                              <Text size="xs" color="muted">{getAuthMethodLabel(entry.auth_method)}</Text>
                              {entry.ip_address && <Text size="xs" color="muted">{entry.ip_address}</Text>}
                              {entry.city && entry.country && (
                                <Text size="xs" color="muted">{entry.city}, {entry.country}</Text>
                              )}
                            </HStack>
                            {entry.failure_reason && (
                              <Text size="xs" color="error">{entry.failure_reason}</Text>
                            )}
                          </VStack>
                          <Text size="xs" color="muted">{formatRelativeDate(entry.created_at)}</Text>
                        </HStack>
                      </div>
                      ))}
                    </VStack>
                  </div>
                )}
              </VStack>
            </Card>
          </VStack>
        </HStack>

        {/* Disable Dialog */}
        <ConfirmDialog
          open={showDisableDialog}
          onClose={() => setShowDisableDialog(false)}
          title={user.is_disabled ? 'Enable User' : 'Disable User'}
          description={
            user.is_disabled
              ? `Re-enable access for ${user.email}? They will be able to log in again.`
              : `Disable ${user.email}? They won't be able to log in until re-enabled.`
          }
          confirmText={user.is_disabled ? 'Enable' : 'Disable'}
          cancelText="Cancel"
          variant={user.is_disabled ? 'default' : 'warning'}
          onConfirm={handleToggleStatus}
          loading={togglingStatus}
        />

        {/* Delete Dialog */}
        <ConfirmDialog
          open={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          title="Delete User"
          description={`Permanently delete ${user.email}? This action cannot be undone. All user data will be removed.`}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={handleDelete}
          loading={deleting}
        />
      </VStack>
    </div>
  )
}
